import { useEffect, useRef, useState } from "react";
import { useMotionValueEvent } from "framer-motion";

/**
 * Drives a <video>'s currentTime from a 0..1 MotionValue.
 *
 * A rAF loop nudges currentTime toward the latest target, but only issues a new
 * seek once the previous one has reported `seeked`. That gives the decoder
 * natural backpressure (no seek pile-up on Safari/iOS) while always making
 * progress — unlike relying on requestVideoFrameCallback, which can stay silent
 * for a paused element in some engines.
 */
export function useScrollScrub({ videoRef, progress, enabled = true }) {
  const [duration, setDuration] = useState(0);
  const targetRef = useRef(0);
  const seekingRef = useRef(false);
  const runningRef = useRef(false);
  const rafRef = useRef(0);
  const lockTimerRef = useRef(0);
  const primedRef = useRef(false);

  // duration + priming + seeked bookkeeping
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;

    const readDuration = () => {
      if (v.duration && Number.isFinite(v.duration)) setDuration(v.duration);
    };
    const onSeeked = () => {
      seekingRef.current = false;
    };

    readDuration();
    v.addEventListener("loadedmetadata", readDuration);
    v.addEventListener("durationchange", readDuration);
    v.addEventListener("seeked", onSeeked);
    try {
      v.load();
    } catch (_) {}

    return () => {
      v.removeEventListener("loadedmetadata", readDuration);
      v.removeEventListener("durationchange", readDuration);
      v.removeEventListener("seeked", onSeeked);
      cancelAnimationFrame(rafRef.current);
      window.clearTimeout(lockTimerRef.current);
      runningRef.current = false;
    };
  }, [videoRef]);

  const tick = () => {
    const v = videoRef.current;
    if (!v) {
      runningRef.current = false;
      return;
    }
    const goal = targetRef.current;
    if (!seekingRef.current && Math.abs(v.currentTime - goal) > 0.01) {
      // cap the jump so a fast flick doesn't ask the decoder for a 5s leap
      const next =
        Math.abs(goal - v.currentTime) > 1
          ? v.currentTime + Math.sign(goal - v.currentTime) * 1
          : goal;
      seekingRef.current = true;
      try {
        v.currentTime = next;
      } catch (_) {
        seekingRef.current = false;
      }
      // safety: if `seeked` never arrives, release the lock
      window.clearTimeout(lockTimerRef.current);
      lockTimerRef.current = window.setTimeout(() => {
        seekingRef.current = false;
      }, 220);
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const ensureRunning = () => {
    if (runningRef.current) return;
    runningRef.current = true;
    rafRef.current = requestAnimationFrame(tick);
  };

  useMotionValueEvent(progress, "change", (p) => {
    const v = videoRef.current;
    if (!enabled || !v || !duration) return;

    if (!primedRef.current) {
      primedRef.current = true;
      const done = () => v.pause();
      const maybe = v.play();
      if (maybe && typeof maybe.then === "function") maybe.then(done).catch(() => {});
      else done();
    }

    const clamped = p < 0 ? 0 : p > 1 ? 1 : p;
    targetRef.current = clamped * duration;
    ensureRunning();
  });

  return { duration };
}
